import type {Context} from '../types';

import {isDynBound} from '@packages/systems/core/utils/DomTypeUtils';

type LinkMode =
  | 'external'
  | 'dynpage'
  | 'page'
  | 'section'
  | 'email'
  | 'tel'
  | 'file';

interface LinkObject {
  mode: LinkMode;
  [key: string]: any;
}

/**
 * Checks whether a Link has extra static properties not in persistedDynboundStaticPropsMap
 */
const linkHasExtraStaticProps = (link: LinkObject): boolean =>
  Object.keys(link).some(
    (k) => link.mode && !persistedDynboundStaticPropsMap[link.mode].includes(k)
  );

/**
 * Static data.link props to persist when dynbinding a URL
 */
const persistedDynboundStaticPropsMap: Record<LinkMode, string[]> = {
  external: ['mode', 'target'],
  dynpage: ['mode', 'target'],
  page: ['mode', 'target', 'pageId'],
  section: ['mode', 'domId'],
  email: ['mode', 'subject'],
  tel: ['mode'],
  file: ['mode', 'target', 'assetId'],
};

const filterDynboundLinkStaticProps = (link: LinkObject) =>
  persistedDynboundStaticPropsMap[link.mode].reduce<Record<string, any>>(
    (newLinkData, key: string) => {
      const value = link[key];
      const hasValue = typeof value !== 'undefined';

      if (hasValue) {
        return {...newLinkData, [key]: value};
      } else {
        return newLinkData;
      }
    },
    {}
  );

export default (context: Context) => {
  const {
    node: {jsonNode},
  } = context;

  const {data = {}} = jsonNode;

  const hasExtraStaticLinkData =
    data && data.link && linkHasExtraStaticProps(data.link);

  const isDynLinkWithExtraData =
    hasExtraStaticLinkData &&
    (isDynBound(data, 'href') || isDynBound(data, 'dataWHref'));

  return {
    needed: isDynLinkWithExtraData,
    logData: {
      msg: 'Remove irrelevant static Link properties from dynamically bound link',
      meta: {
        nodeId: jsonNode._id,
      },
    },
    apply: () => {
      data.link = filterDynboundLinkStaticProps(data.link);
    },
  };
};
